import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight } from "lucide-react";
import { collections } from "../data/site";
import { MaskedLines } from "./Reveal";

// Choose slides with strong lifestyle photography from catalogue
const HERO_SLIDES = [
  {
    slug: "vesna",
    eyebrow: "01 — Koleksiyon",
    title: ["Zamansız yüzeyler,", "kusursuz mekânlar."],
    subtitle:
      "Geniş formatlı porselen dilimlerin sessiz dili. Yeni koleksiyon Vesna, ışığı yumuşatıyor.",
    image: "/hero-slide-1.jpg",
    imageMobile: "/hero-slide-1-mobile.jpg",
  },
  {
    slug: "calacatta",
    eyebrow: "02 — İtalyan Ustalık",
    title: ["Mermerin hikâyesi,", "altın damarların cesareti."],
    subtitle:
      "Calacatta koleksiyonu — beyazın içinde yanan altın çizgiler. Salon zeminlerinin klasiği.",
    image: "/hero-slide-2.jpg",
    imageMobile: "/hero-slide-2-mobile.jpg",
  },
  {
    slug: "riva",
    eyebrow: "03 — Dış Mekan",
    title: ["Doğal formda", "dayanıklı estetik."],
    subtitle:
      "20 mm dış mekân porseleni Riva — donma-çözünme direnci ve mat karakterle terasınıza uzun ömür.",
    image: "/hero-slide-3.jpg",
    imageMobile: "/hero-slide-3-mobile.jpg",
  },
  {
    slug: "showroom",
    eyebrow: "SeraPark Showroom",
    title: ["Yeniliklerin", "Öncüsüyüz."],
    subtitle:
      "Şanlıurfa’nın en kapsamlı seramik ve porselen showroomunda, dünya markalarını ve son trendleri bir araya getiriyoruz.",
    image: "/hero-slide-4.jpg",
    duration: 8500,
  },
];

export default function Hero() {
  const [i, setI] = useState(0);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const id = setInterval(
      () => setI((v) => (v + 1) % HERO_SLIDES.length),
      HERO_SLIDES[i].duration || 6500
    );
    return () => clearInterval(id);
  }, [i]);

  useEffect(() => {
    const onS = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onS, { passive: true });
    return () => window.removeEventListener("scroll", onS);
  }, []);

  const active = HERO_SLIDES[i];
  const activeCol = collections.find((c) => c.slug === active.slug);

  return (
    <section
      data-testid="hero"
      className="relative h-[100svh] min-h-[720px] w-full overflow-hidden bg-[#0e0e0e] text-white"
    >
      {/* Background image with parallax */}
      <AnimatePresence mode="wait">
        <motion.div
          key={active.slug}
          initial={{ opacity: 0, scale: 1.08 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 1 }}
          transition={{ duration: 1.6, ease: [0.2, 0.7, 0.2, 1] }}
          className="absolute inset-0"
          style={{ transform: `translate3d(0, ${scrollY * 0.15}px, 0)` }}
        >
          <img
            src={active.image || activeCol?.hero}
            alt={activeCol?.name || "SeraPark"}
            className="hidden md:block w-full h-full object-cover"
          />
          <img
            src={active.imageMobile || active.image || activeCol?.hero}
            alt={activeCol?.name || "SeraPark"}
            className="md:hidden w-full h-full object-cover"
          />
          {/* Editorial dark gradient */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0e0e0e] via-[#0e0e0e]/45 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0e0e0e]/70 via-transparent to-transparent" />
        </motion.div>
      </AnimatePresence>

      {/* Grain */}
      <div className="grain absolute inset-0 pointer-events-none" />

      {/* Vertical index line right */}
      <div className="hidden md:flex absolute right-8 top-1/2 -translate-y-1/2 flex-col items-end gap-4 z-10">
        {HERO_SLIDES.map((s, idx) => (
          <button
            key={s.slug}
            onClick={() => setI(idx)}
            data-testid={`hero-dot-${idx}`}
            className="flex items-center gap-3 group"
          >
            <span
              className={`text-[10px] tracking-[0.3em] transition-opacity ${
                idx === i ? "opacity-100" : "opacity-40"
              }`}
            >
              0{idx + 1}
            </span>
            <span
              className={`h-[1px] transition-all ${
                idx === i ? "w-14 bg-[#B8860B]" : "w-8 bg-white/40"
              }`}
            />
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 lg:px-10 flex items-end pb-24 md:pb-32">
        <div className="max-w-3xl">
          <AnimatePresence mode="wait">
            <motion.div
              key={active.slug + "-eyebrow"}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.8 }}
              className="text-[11px] tracking-[0.3em] uppercase text-[#B8860B] mb-6"
            >
              {active.eyebrow}
            </motion.div>
          </AnimatePresence>

          <AnimatePresence mode="wait">
            <motion.div key={active.slug + "-title"}>
              <MaskedLines
                as="h1"
                lines={active.title}
                lineClassName="text-white font-serif-display text-[42px] sm:text-[64px] md:text-[80px] lg:text-[92px] leading-[1.02] tracking-[-0.02em]"
                stagger={0.09}
                duration={1.1}
              />
            </motion.div>
          </AnimatePresence>

          <AnimatePresence mode="wait">
            <motion.p
              key={active.slug + "-sub"}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.9, delay: 0.35 }}
              className="mt-8 text-white/70 text-base md:text-lg max-w-xl leading-relaxed"
            >
              {active.subtitle}
            </motion.p>
          </AnimatePresence>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65 }}
            className="mt-10 flex flex-wrap items-center gap-3"
          >
            <Link
              to="/urunler"
              data-testid="hero-cta-products"
              className="btn-gold"
            >
              Ürünleri İncele <ArrowRight size={14} />
            </Link>
            <Link
              to="/teklif-al"
              data-testid="hero-cta-quote"
              className="btn-ghost btn-light-ghost"
            >
              Teklif Al
            </Link>
            <Link
              to="/iletisim"
              data-testid="hero-cta-contact"
              className="btn-ghost btn-light-ghost"
            >
              Showroom Ziyareti
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Bottom badge — featured collection */}
      {activeCol && (
        <div className="absolute bottom-6 left-6 md:left-10 z-10 flex items-center gap-4">
          <div className="h-[1px] w-12 bg-white/30" />
          <div className="flex items-center gap-3 text-[11px] tracking-[0.25em] uppercase text-white/70">
            <span>Koleksiyon:</span>
            <Link
              to={`/urunler/${active.slug}`}
              className="text-white hover:text-[#B8860B] transition-colors flex items-center gap-1.5"
              data-testid="hero-featured-link"
            >
              {activeCol?.name} <ChevronRight size={12} />
            </Link>
          </div>
        </div>
      )}

      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-6 right-6 md:right-10 z-10 flex flex-col items-center gap-2 text-white/50"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase">Kaydırın</span>
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-[1px] h-8 bg-[#B8860B]"
        />
      </motion.div>
    </section>
  );
}
